import { useEffect, useMemo, useState } from "react";
import type { MentionProvider } from "@lumanity/ui/chat";
import { ChevronRight } from "lucide-react";
import AddedEvidenceList from "@/components/evidenceGathering/AddedEvidenceList";
import EmptyEvidenceState from "@/components/evidenceGathering/EmptyEvidenceState";
import EvidenceCatalogDialog from "@/components/evidenceGathering/EvidenceCatalogDialog";
import UploadDocument from "@/components/UploadDocument";
import { Button } from "@/components/ui/button";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { DocumentUpdatePayload, useDocuments, useUpdateDocument } from "@/hooks/useDocumentMetadata";
import { useDeleteProjectDocuments } from "@/hooks/useProjects";

const EvidenceGatheringStep = () => {
  const { projectId, project, projectDocuments, activeStage, handleStageComplete, continueLabel, setMentionProvider } = useProjectDetailContext();
  const { data: documents = [], isLoading, isError, error } = useDocuments();
  const updateDocument = useUpdateDocument();
  const deleteProjectDocuments = useDeleteProjectDocuments();
  const { openDocument } = useDocumentViewer();

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingDropFiles, setPendingDropFiles] = useState<File[] | undefined>(undefined);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const linkedIds = useMemo(() => new Set(projectDocuments.map((d) => d.id)), [projectDocuments]);

  // Rows this project already links, plus anything with its client id. No global
  // pool: a Private doc arrives only via the link written at upload, so the set
  // varies by project.
  const evidenceGatheringDocuments = useMemo(() => documents.filter((d) => {
    if (linkedIds.has(d.id)) return true;
    return Boolean(project?.clientIds?.[0]) && d.clientId === project.clientIds[0];
  }), [documents, linkedIds, project?.clientIds]);

  const handleUpdateDocument = (
    payload: { id: string } & DocumentUpdatePayload,
    options?: { onSuccess?: () => void }
  ) => {
    updateDocument.mutate(payload, { onSuccess: () => options?.onSuccess?.() });
  };

  const handleRemove = (documentId: string) => {
    if (!projectId) return;
    setRemovingIds((prev) => new Set(prev).add(documentId));
    deleteProjectDocuments.mutate(
      { projectId, documentIds: [documentId] },
      {
        onSettled: () =>
          setRemovingIds((prev) => {
            const next = new Set(prev);
            next.delete(documentId);
            return next;
          }),
      }
    );
  };

  const openUploadWithFiles = (files: File[]) => {
    setPendingDropFiles(files);
    setUploadOpen(true);
  };

  const hasDocuments = projectDocuments.length > 0;

  // @-mention provider: resolved fresh at send time (see useAssistantThread in
  // @lumanity/ui), so content is always current — never frozen at mention time.
  const mentionProvider = useMemo<MentionProvider>(() => ({
    search: async (query) => {
      const q = query.toLowerCase();
      return projectDocuments
        .filter((d) => (d.title || d.filename || "").toLowerCase().includes(q))
        .slice(0, 20)
        .map((d) => ({ id: d.id, label: d.title || d.filename || d.id, description: d.filename }));
    },
    resolveContent: (item) => {
      const doc = projectDocuments.find((d) => d.id === item.id);
      return [doc?.title || doc?.filename || item.label, doc?.year, doc?.sourceOrganisation]
        .filter(Boolean)
        .join(", ");
    },
  }), [projectDocuments]);

  useEffect(() => {
    setMentionProvider(mentionProvider);
    return () => setMentionProvider(null);
  }, [mentionProvider, setMentionProvider]);

  return (
    <div>
      <div className={hasDocuments ? "flex items-start justify-between" : "flex flex-col items-center text-center"}>
        <div>
          <h2 className="text-lg font-bold text-foreground">Add Evidence</h2>
          <p className="text-sm text-muted-foreground mb-4">How would you like to add documents to this project?</p>
        </div>
        {hasDocuments && (
          <div className="flex items-center gap-2">
            <Button type="button" variant="brand" onClick={() => setCatalogOpen(true)}>
              Select from Evidence Base
            </Button>
            <Button type="button" variant="outline" onClick={() => setUploadOpen(true)}>
              Upload Documents
            </Button>
          </div>
        )}
      </div>

      {hasDocuments ? (
        <AddedEvidenceList
          documents={projectDocuments}
          onRemove={handleRemove}
          onView={(doc) => openDocument({ doc })}
          removingIds={removingIds}
        />
      ) : (
        <EmptyEvidenceState onBrowseCatalog={() => setCatalogOpen(true)} onFilesSelected={openUploadWithFiles} />
      )}

      <div className="flex justify-end mt-6">
        <Button
          type="button"
          variant="brand"
          disabled={!hasDocuments}
          onClick={() => handleStageComplete(activeStage)}
        >
          {continueLabel} <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <EvidenceCatalogDialog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        documents={evidenceGatheringDocuments}
        projectDocumentIds={Array.from(linkedIds)}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onUpdateDocument={handleUpdateDocument}
        isUpdating={updateDocument.isPending}
        projectId={projectId}
        projectModel={project?.model}
      />

      <UploadDocument
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) setPendingDropFiles(undefined);
        }}
        initialFiles={pendingDropFiles}
        showEvidenceBaseEscapeHatch={false}
      />
    </div>
  );
};

export default EvidenceGatheringStep;
